import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/feature/Footer";
import { getPrivateConversation, getPrivateConversations, getPrivateMessages, markPrivateConversationAsRead, sendPrivateMessage, } from "@/services/activityService";
import { PrivateConversation, PrivateMessage, } from "@/services/activityService";

export default function Messages() {
    const { conversationId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [conversation, setConversation] = useState<PrivateConversation | null>(null);
    const [conversations, setConversations] = useState<PrivateConversation[]>([]);
    const [messages, setMessages] = useState<PrivateMessage[]>([]);
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);



    useEffect(() => {
        if (conversationId) return;

        const loadConversations = async () => {
            try {
                setLoading(true);
                setError(null);

                const data = await getPrivateConversations();
                setConversations(data);
            } catch {
                setError("Impossible de charger tes conversations.");
            } finally {
                setLoading(false);
            }
        };

        loadConversations();
    }, [conversationId]);

    useEffect(() => {
        if (!conversationId) return;

        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                const [conversationData, messagesData] = await Promise.all([
                    getPrivateConversation(conversationId),
                    getPrivateMessages(conversationId),
                ]);

                setConversation(conversationData);
                setMessages(messagesData);

                await markPrivateConversationAsRead(conversationId);
            } catch {
                setError("Impossible de charger cette conversation.");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [conversationId]);

    const handleSend = async () => {
        if (!conversationId || !content.trim() || sending) return;

        try {
            setSending(true);

            const message = await sendPrivateMessage(
                conversationId,
                content.trim()
            );

            setMessages((current) => [...current, message]);
            setContent("");
        } catch {
            setError("Impossible d’envoyer le message.");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background-50">
                <p className="text-foreground-500">Chargement…</p>
            </div>
        );
    }

    if (!conversationId) {
        return (
            <div className="min-h-screen flex flex-col bg-background-50">
                <header className="sticky top-0 z-20 bg-white border-b border-background-200">
                    <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="text-foreground-500 hover:text-foreground-900 cursor-pointer"
                            aria-label="Retour aux activités"
                        >
                            <i className="ri-arrow-left-line text-xl" />
                        </button>
                        <div className="w-8 h-8 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center shrink-0">
                            <i className="ri-user-line text-sm" />
                        </div>

                        <div className="min-w-0">
                            <h1 className="text-sm font-semibold text-foreground-900 truncate">
                                {conversation?.otherUserName}
                            </h1>
                            <p className="text-xs text-foreground-400 truncate">
                                {conversation?.activityTitle}
                            </p>
                        </div>
                    </div>
                </header>

                <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-6">
                    {error && (
                        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {conversations.length === 0 ? (
                        <div className="bg-white border border-background-200 rounded-lg p-8 text-center">
                            <i className="ri-message-3-line text-3xl text-foreground-300" />
                            <p className="mt-3 text-sm text-foreground-500">
                                Aucun message pour le moment.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-white border border-background-200 rounded-lg divide-y divide-background-200">
                            {conversations.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => navigate(`/messages/${item.id}`)}
                                    className="w-full text-left px-4 py-4 hover:bg-background-50 transition-colors cursor-pointer"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                                            <i className="ri-user-line" />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <h2 className="font-semibold text-sm text-foreground-900 truncate">
                                                    {item.otherUserName}
                                                </h2>

                                                {item.unreadCount > 0 && (
                                                    <span className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-primary-500 text-white text-xs font-semibold flex items-center justify-center">
                                                        {item.unreadCount}
                                                    </span>
                                                )}
                                            </div>

                                            <p className="text-xs text-foreground-500 truncate mt-0.5">
                                                {item.activityTitle}
                                            </p>

                                            {item.lastMessage && (
                                                <p className="text-sm text-foreground-600 truncate mt-1">
                                                    {item.lastMessage}
                                                </p>
                                            )}
                                        </div>

                                        <i className="ri-arrow-right-s-line text-foreground-400 mt-2" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </main>

                <Footer />
            </div>
        );
    }

    if (!conversation) {
        return (
            <div className="min-h-screen flex flex-col bg-background-50">
                <main className="flex-1 px-4 py-8">
                    <div className="max-w-3xl mx-auto">
                        <p className="text-foreground-600">
                            Conversation introuvable.
                        </p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-background-50">
            <header className="sticky top-0 z-20 bg-white border-b border-background-200">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="text-foreground-500 hover:text-foreground-900 cursor-pointer"
                        aria-label="Retour aux messages"
                    >
                        <i className="ri-arrow-left-line text-xl" />
                    </button>

                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center shrink-0">
                            <i className="ri-chat-3-line text-sm" />
                        </div>

                        <h1 className="text-sm md:text-base font-normal text-foreground-900">
                            Conversation avec{" "}
                            <span className="font-bold text-foreground-950">
                                {conversation.otherUserName}
                            </span>
                        </h1>
                    </div>
                </div>
            </header>
            <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-6">
                {error && (
                    <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="bg-white border border-background-200 rounded-lg overflow-hidden flex flex-col min-h-[400px]">
                    <div className="flex-1 max-h-[60vh] overflow-y-auto px-4 py-4 flex flex-col gap-4 md:px-5">
                        {messages.length === 0 ? (
                            <div className="text-center py-10">
                                <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-2">
                                    <i className="ri-chat-new-line text-accent-500 text-lg" />
                                </div>

                                <p className="text-sm text-foreground-500">
                                    Commencez la conversation avec l'organisateur de cette activité.
                                </p>
                            </div>
                        ) : (
                            messages.map((message) => {
                                const isMine = message.senderId === user?.id;

                                return (
                                    <div
                                        key={message.id}
                                        className={`group flex gap-2 ${isMine ? "justify-end" : "justify-start"
                                            }`}
                                    >
                                        {!isMine && (
                                            <div className="w-8 h-8 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center shrink-0 text-xs font-bold">
                                                {message.senderName
                                                    .split(" ")
                                                    .map((part) => part[0])
                                                    .join("")
                                                    .slice(0, 2)
                                                    .toUpperCase()}
                                            </div>
                                        )}

                                        <div
                                            className={`max-w-[80%] min-w-0 ${isMine ? "items-end" : "items-start"
                                                }`}
                                        >
                                            <p className="text-xs font-medium text-foreground-400 mb-1 px-1">
                                                {isMine ? "Moi" : message.senderName}
                                            </p>

                                            <div
                                                className={`rounded-xl px-3 py-2.5 ${isMine
                                                    ? "bg-primary-500 text-white"
                                                    : "bg-background-100 text-foreground-900"
                                                    }`}
                                            >
                                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                                    {message.content}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="border-t border-background-200 bg-white px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:px-5 md:py-4">
                        <div className="flex items-center gap-2">
                            <input
                                value={content}
                                onChange={(event) => setContent(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && !event.shiftKey) {
                                        event.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder="Écrire un message…"
                                maxLength={5000}
                                className="flex-1 min-w-0 px-4 py-3 rounded-lg border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                            />

                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={sending || !content.trim()}
                                className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="ri-send-plane-fill" />
                                <span className="hidden md:inline">
                                    Envoyer
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}